import { ErrorMessageInput } from '@/components/common';
import clsx from 'clsx';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { createPortal } from 'react-dom';
import { commonsService } from '@/api';
import useDeviceWidth from '@/hooks/useDeviceWidth';

interface Props {
	name: string;
	onSelect?: (address: AddressValue) => void;
	placeholder?: string;
	className?: string;
	title?: string;
	disabled?: boolean;
	error?: any;
	rules: any;
	register: UseFormRegister<FieldValues>;
	setValueInput: UseFormSetValue<FieldValues>;
	defaultValue?: string;
	/** When `street`, persist only street line in `name`. Default keeps the full formatted string. */
	valueMode?: 'full' | 'street';
}

export interface AddressValue {
	street: string;
	/** Google `subpremise` (apt, suite, unit). Empty when the place has none. */
	address2: string;
	city: string;
	state: string;
	zip: string;
	country: string;
	fullAddress?: string;
}

export const InputPlacesAutocomplete: FC<Props> = ({
	name,
	onSelect,
	className,
	disabled,
	placeholder,
	title,
	error,
	register,
	rules,
	setValueInput,
	defaultValue,
	valueMode = 'full',
}) => {
	const [value, setValue] = useState(defaultValue || '');
	const [suggestions, setSuggestions] = useState<any[]>([]);
	const [open, setOpen] = useState(false);
	const [rect, setRect] = useState<DOMRect | null>(null);

	const selectedAddressRef = useRef<AddressValue | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout>();

	const width = useDeviceWidth();
	const isMobile = width < 650;

	// Detect click outside to close dropdown
	const containerRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Sync defaultValue to value state if it changes
	useEffect(() => {
		if (defaultValue) {
			setValue(defaultValue);
			selectedAddressRef.current = {
				fullAddress: defaultValue,
			} as AddressValue;
		}
	}, [defaultValue]);

	// Fetch suggestions
	useEffect(() => {
		// If the value matches a selected address, do not search
		if (
			selectedAddressRef.current &&
			value === selectedAddressRef.current.fullAddress
		) {
			setOpen(false);
			return;
		}

		if (!value || value.length < 3) {
			setSuggestions([]);
			setOpen(false);
			selectedAddressRef.current = null; // reset selection if user types manually
			return;
		}

		clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(async () => {
			const results = await commonsService.getAutompletePlaces(value);
			setSuggestions(results.data);
			setOpen(results.data.length > 0);
		}, 300);
	}, [value]);

	const handleSelect = async (placeId: string, description: string) => {
		const details = await commonsService.getPlaceDetails(placeId);
		const components = details.data.addressComponents ?? [];

		const get = (type: string) =>
			components.find((c: any) => c.types.includes(type))?.longText ?? '';

		const address: AddressValue = {
			street: `${get('street_number')} ${get('route')}`.trim(),
			address2: get('subpremise'),
			city:
				get('locality') ||
				get('sublocality_level_1') ||
				get('administrative_area_level_3') ||
				get('neighborhood'),
			state: get('administrative_area_level_1'),
			zip: get('postal_code'),
			country: get('country'),
			fullAddress: description,
		};

		const stringAddress = [
			address.street,
			address.city,
			address.state,
			address.country,
			address.zip,
		].join(', ');

		const persistedValue =
			valueMode === 'street' ? address.street : stringAddress;

		selectedAddressRef.current = { ...address, fullAddress: persistedValue };
		setValueInput(name, persistedValue, { shouldValidate: true });
		if (address.zip) {
			setValueInput('zipCode', address.zip, { shouldValidate: true });
		}
		setValue(persistedValue);
		setOpen(false);
		setSuggestions([]);

		onSelect?.(address);
	};

	useEffect(() => {
		if (open && inputRef.current) {
			setRect(inputRef.current.getBoundingClientRect());
		}
	}, [open]);

	const isRequired =
		rules?.required === true || rules?.required?.value === true;

	const finalRules = useMemo(() => {
		return {
			...rules,
			validate: (inputValue: string) => {
				if (!inputValue || inputValue.trim() === '')
					return isRequired ? 'This field is required.' : true;
				if (
					inputValue &&
					suggestions.length === 0 &&
					!selectedAddressRef.current
				)
					return 'Please enter a valid address.';
				if (
					inputValue.length > 3 &&
					(!selectedAddressRef.current ||
						inputValue !== selectedAddressRef.current.fullAddress)
				)
					return 'Please select an address from the list.';

				return true;
			},
		};
	}, [rules, isRequired]);

	const registerAux = register(name, finalRules);

	const InputPlace = () => {
		return (
			<div className="max-h-32 md:max-h-60 overflow-auto rounded-xl border border-opacity-primary bg-white shadow-shadow2">
				<ul>
					{suggestions.map((s, i) => (
						<li
							key={i}
							onMouseDown={() =>
								handleSelect(
									s.placePrediction.placeId,
									s.placePrediction.text.text
								)
							}
							className="cursor-pointer px-3 lg:px-4 py-2 lg:py-3 hover:bg-gray-100 text-left text-sm lg:text-base"
						>
							{s.placePrediction.text.text}
						</li>
					))}
				</ul>
			</div>
		);
	};

	return (
		<div ref={containerRef} className="relative w-full">
			{title && (
				<label
					htmlFor={name}
					className="block text-base text-left text-black capitalize"
				>
					{title}
				</label>
			)}

			<input
				id={name}
				name={name}
				value={value}
				disabled={disabled}
				placeholder={placeholder}
				autoComplete="off"
				className={clsx(
					'px-6 mt-2 py-3 w-full text-base font-sans border rounded-full bg-white',
					'focus:outline-none focus:ring-0 placeholder:text-base text-black placeholder-opacity-primary',
					{
						'border-main-red text-main-red': error,
						'border-opacity-primary': !error,
					},
					className
				)}
				ref={(el) => {
					inputRef.current = el;
					if (typeof registerAux.ref === 'function') registerAux.ref(el);
				}}
				onChange={(e) => {
					registerAux.onChange(e);
					setValue(e.target.value);
					selectedAddressRef.current = null; // reset if user types manually
				}}
			/>

			{error?.message && <ErrorMessageInput message={error.message} />}

			{/* DROPDOWN */}
			{open ? (
				isMobile ? (
					<div
						className={clsx('absolute left-0 right-0 top-full z-50', {
							'-mt-5': error,
						})}
					>
						<InputPlace />
					</div>
				) : (
					rect &&
					createPortal(
						<div
							style={{
								position: 'absolute',
								top: rect.bottom + 8,
								left: rect.left,
								width: rect.width,
								zIndex: 9999,
							}}
						>
							<InputPlace />
						</div>,
						document.body
					)
				)
			) : null}
		</div>
	);
};
